import type { FormControlProps, SelectChangeEvent } from '@mui/material'
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { logger } from '@tunasong/models'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { usePermission } from './audio-permissions.hook.js'
import { getDevices } from './devices.js'

export interface DeviceSelectProps extends Omit<FormControlProps, 'onChange'> {
  className?: string
  type: MediaDeviceKind
  selectedId?: string | null
  onChange(selected: MediaDeviceInfo): void
}

const labels: Record<MediaDeviceKind, string> = {
  audioinput: 'Audio Input',
  audiooutput: 'Audio Output',
  videoinput: 'Video Input',
}

export const DeviceSelect: FC<DeviceSelectProps> = props => {
  const { onChange, selectedId = 'default', type, ...formControlProps } = props

  const [devices, setDevices] = useState<MediaDeviceInfo[] | null>(null)
  const { permission } = usePermission('microphone')

  useEffect(() => {
    // Subscribe to device changes
    const handleDeviceChange = () => {
      getDevices(type).then(devices => {
        logger.debug(`${type} devices available`, devices)
        setDevices(devices)
      })
    }
    // Initial load when microphone permission is granted
    if (permission === 'granted' || type === 'audiooutput') {
      handleDeviceChange()
    }
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange)
    }
  }, [type, permission])

  const handleChange = (event: SelectChangeEvent<string | null>) => {
    const val = event.target.value as string
    const d = devices?.find(item => item.deviceId === val)
    if (d) {
      onChange(d)
    }
  }

  const label = labels[type]

  const validDevices = devices?.filter(d => d.deviceId)

  if (!validDevices || validDevices.length === 0) {
    return <Typography variant="caption">No {label} devices available</Typography>
  }

  return (
    <FormControl fullWidth={true} {...formControlProps} sx={{ mt: 1, ...props.sx }}>
      <InputLabel htmlFor={`${type}-select`}>{label}</InputLabel>
      <Select
        fullWidth={true}
        label={label}
        inputProps={{
          id: `${type}-select`,
        }}
        value={selectedId}
        onChange={handleChange}
      >
        {validDevices.map(device => (
          <MenuItem key={device.deviceId} value={device.deviceId}>
            {device.deviceId === 'default' ? 'Default' : device.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default DeviceSelect
